import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Collapse,
  TextField,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Search as SearchIcon,
  AccountBalance as AccountIcon,
  TrendingUp as IncomeIcon,
  TrendingDown as ExpenseIcon,
  AccountBalanceWallet as AssetIcon,
  CreditCard as LiabilityIcon,
  Savings as EquityIcon
} from '@mui/icons-material';
import { toast } from 'react-hot-toast';
import AddAccountDialog from './AddAccountDialog';
import EditAccountDialog from './EditAccountDialog';

// Types
interface Account {
  id: number;
  code: string;
  name: string;
  account_type: string;
  parent_id?: number;
  vat_rate: string;
  is_vat_applicable: boolean;
  is_active: boolean;
  current_balance: number;
  balance_formatted: string;
  vat_rate_percentage: number;
  account_type_display: string;
  full_name: string;
  level: number;
  has_children: boolean;
}

interface ChartOfAccountsData {
  accounts: Account[];
  summary: {
    total_accounts: number;
    active_accounts: number;
    by_type: Record<string, { count: number; balance: number }>;
    total_assets: number;
    total_liabilities: number;
    total_equity: number;
    total_income: number;
    total_expenses: number;
  };
  hierarchy: Record<string, any>;
}

const ChartOfAccounts: React.FC = () => {
  const [data, setData] = useState<ChartOfAccountsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<string>('all');
  const [expandedAccounts, setExpandedAccounts] = useState<Set<number>>(new Set());
  const [showInactive, setShowInactive] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState<Account | null>(null);

  // Fetch chart of accounts data
  const fetchChartOfAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/accounts/');

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      setData(result);
      setError(null);
    } catch (err) {
      console.error('Error fetching chart of accounts:', err);
      setError(err instanceof Error ? err.message : 'Failed to load chart of accounts');
      toast.error('Failed to load chart of accounts');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchChartOfAccounts();
  }, []);

  // Initialize US Standard Chart of Accounts
  const initializeStandardChart = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/v1/accounts/initialize-uk-standard', {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      toast.success(`${result.accounts_created} accounts created successfully!`);
      await fetchChartOfAccounts(); // Refresh data
    } catch (err) {
      console.error('Error initializing chart:', err);
      toast.error('Failed to initialize chart of accounts');
    } finally {
      setLoading(false);
    }
  };

  // Get account type icon
  const getAccountTypeIcon = (accountType: string) => {
    switch (accountType.toLowerCase()) {
      case 'asset':
        return <AssetIcon color="primary" />;
      case 'liability':
        return <LiabilityIcon color="error" />;
      case 'equity':
        return <EquityIcon color="secondary" />;
      case 'income':
        return <IncomeIcon color="success" />;
      case 'expense':
        return <ExpenseIcon color="warning" />;
      default:
        return <AccountIcon />;
    }
  };

  // Get account type color
  const getAccountTypeColor = (accountType: string) => {
    switch (accountType.toLowerCase()) {
      case 'asset':
        return 'primary';
      case 'liability':
        return 'error';
      case 'equity':
        return 'secondary';
      case 'income':
        return 'success';
      case 'expense':
        return 'warning';
      default:
        return 'default';
    }
  };

  // Filter accounts based on search and type
  const filteredAccounts = data?.accounts.filter(account => {
    const matchesSearch = account.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      account.code.includes(searchTerm);
    const matchesType = filterType === 'all' || account.account_type === filterType;
    const matchesActive = showInactive || account.is_active;

    return matchesSearch && matchesType && matchesActive;
  }) || [];

  // Toggle account expansion
  const toggleAccountExpansion = (accountId: number) => {
    const newExpanded = new Set(expandedAccounts);
    if (newExpanded.has(accountId)) {
      newExpanded.delete(accountId);
    } else {
      newExpanded.add(accountId);
    }
    setExpandedAccounts(newExpanded);
  };

  // Handle edit account
  const handleEditAccount = (account: Account) => {
    setSelectedAccount(account);
    setEditDialogOpen(true);
  };

  // Handle account dialogs
  const handleAccountAdded = () => {
    fetchChartOfAccounts();
  };

  const handleAccountUpdated = () => {
    fetchChartOfAccounts();
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!data) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        No chart of accounts data available.
      </Alert>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" component="h1" fontWeight="bold">
          Chart of Accounts
        </Typography>
        <Box display="flex" gap={2}>
          {data.summary.total_accounts === 0 && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<AddIcon />}
              onClick={initializeStandardChart}
            >
              Initialize US Standard Chart
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAddDialogOpen(true)}
          >
            Add Account
          </Button>
        </Box>
      </Box>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={3}>
        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <AssetIcon color="primary" />
                <Box>
                  <Typography variant="h6" color="primary">
                    ${data.summary.total_assets.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Assets
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <LiabilityIcon color="error" />
                <Box>
                  <Typography variant="h6" color="error">
                    ${data.summary.total_liabilities.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Liabilities
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <EquityIcon color="secondary" />
                <Box>
                  <Typography variant="h6" color="secondary">
                    ${data.summary.total_equity.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Equity
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <IncomeIcon color="success" />
                <Box>
                  <Typography variant="h6" color="success">
                    ${data.summary.total_income.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Income
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} md={2.4}>
          <Card>
            <CardContent>
              <Box display="flex" alignItems="center" gap={1}>
                <ExpenseIcon color="warning" />
                <Box>
                  <Typography variant="h6" color="warning">
                    ${data.summary.total_expenses.toLocaleString()}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Expenses
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Filters and Search */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              placeholder="Search accounts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>Account Type</InputLabel>
              <Select
                value={filterType}
                label="Account Type"
                onChange={(e) => setFilterType(e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="asset">Assets</MenuItem>
                <MenuItem value="liability">Liabilities</MenuItem>
                <MenuItem value="equity">Equity</MenuItem>
                <MenuItem value="income">Income</MenuItem>
                <MenuItem value="expense">Expenses</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              variant={showInactive ? "contained" : "outlined"}
              onClick={() => setShowInactive(!showInactive)}
              fullWidth
            >
              {showInactive ? "Hide Inactive" : "Show Inactive"}
            </Button>
          </Grid>

          <Grid item xs={12} md={2}>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              {filteredAccounts.length} of {data.summary.total_accounts} accounts
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Accounts Table */}
      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Account</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Sales Tax Rate</TableCell>
                <TableCell align="right">Balance</TableCell>
                <TableCell>Status</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAccounts.map((account) => (
                <TableRow
                  key={account.id}
                  sx={{
                    opacity: account.is_active ? 1 : 0.6,
                    '&:hover': { backgroundColor: 'action.hover' }
                  }}
                >
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      {/* Hierarchy indentation */}
                      <Box sx={{ width: account.level * 20 }} />

                      {/* Expand/collapse for parent accounts */}
                      {account.has_children && (
                        <IconButton
                          size="small"
                          onClick={() => toggleAccountExpansion(account.id)}
                        >
                          {expandedAccounts.has(account.id) ?
                            <ExpandLessIcon /> : <ExpandMoreIcon />
                          }
                        </IconButton>
                      )}

                      {/* Account type icon */}
                      {getAccountTypeIcon(account.account_type)}

                      {/* Account details */}
                      <Box>
                        <Typography variant="body1" fontWeight="medium">
                          {account.code} - {account.name}
                        </Typography>
                        {account.level > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            Level {account.level} account
                          </Typography>
                        )}
                      </Box>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={account.account_type_display}
                      color={getAccountTypeColor(account.account_type) as any}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>

                  <TableCell>
                    {account.is_vat_applicable ? (
                      <Chip
                        label={`${account.vat_rate_percentage}%`}
                        size="small"
                        color={account.vat_rate_percentage > 0 ? "primary" : "default"}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        N/A
                      </Typography>
                    )}
                  </TableCell>

                  <TableCell align="right">
                    <Typography
                      variant="body1"
                      fontWeight="medium"
                      color={account.current_balance >= 0 ? "text.primary" : "error"}
                    >
                      {account.balance_formatted}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={account.is_active ? "Active" : "Inactive"}
                      color={account.is_active ? "success" : "default"}
                      size="small"
                    />
                  </TableCell>

                  <TableCell align="center">
                    <Box display="flex" justifyContent="center" gap={1}>
                      <Tooltip title="Edit Account">
                        <IconButton
                          size="small"
                          onClick={() => handleEditAccount(account)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>

                      <Tooltip title={account.is_active ? "Deactivate Account" : "Activate Account"}>
                        <IconButton
                          size="small"
                          color={account.is_active ? "error" : "success"}
                          onClick={() => handleEditAccount(account)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}

              {filteredAccounts.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <Typography variant="body1" color="text.secondary">
                      {data.summary.total_accounts === 0
                        ? "No accounts found. Initialize the US Standard Chart to get started."
                        : "No accounts match your search criteria."
                      }
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Add Account Dialog */}
      <AddAccountDialog
        open={addDialogOpen}
        onClose={() => setAddDialogOpen(false)}
        onAccountAdded={handleAccountAdded}
        accounts={data?.accounts || []}
      />

      {/* Edit Account Dialog */}
      <EditAccountDialog
        open={editDialogOpen}
        onClose={() => {
          setEditDialogOpen(false);
          setSelectedAccount(null);
        }}
        onAccountUpdated={handleAccountUpdated}
        account={selectedAccount}
        accounts={data?.accounts || []}
      />
    </Box>
  );
};

export default ChartOfAccounts;
